/**
 * MnbPlayer - Main player component
 * Orchestrates video playback with HLS.js for Smart TVs
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { usePlayer, usePlayerState } from '../core/PlayerContext';
import { HLSProvider } from '../providers/HLSProvider';
import { VideoProvider } from '../providers/VideoProvider';

function isSubtitleOffValue(value) {
    var normalized;

    if (value === false) return true;
    if (value == null) return false;

    normalized = String(value).toLowerCase();
    return (
        normalized === 'off' ||
        normalized === 'none' ||
        normalized === 'disabled' ||
        normalized === 'false' ||
        normalized === '0' ||
        normalized === '-1' ||
        normalized === 'خاموش'
    );
}

function normalizeLang(value) {
    return value == null ? '' : String(value).toLowerCase();
}

function matchesSubtitleLang(item, behaviorLang) {
    var srcLang = normalizeLang(item.srcLang || item.srclang || item.lang);
    var label = normalizeLang(item.label);
    var lang = normalizeLang(item.lang);
    var target = normalizeLang(behaviorLang);
    var targetBase = target.split('-')[0];

    if (!target) return false;
    if (srcLang === target || label === target || lang === target) return true;
    if (targetBase && (srcLang === targetBase || lang === targetBase || label === targetBase)) return true;
    return false;
}

function applySubtitleCueStyle(fontSize, textColor, bgColor) {
    var fontSizePx;
    var style;

    if (typeof document === 'undefined') return;

    fontSizePx = Math.round(40 * fontSize);
    style = document.getElementById('mnb-cue-style');
    if (!style) {
        style = document.createElement('style');
        style.id = 'mnb-cue-style';
        document.head.appendChild(style);
    }

    style.textContent =
        'video::cue {' +
        'color: ' + textColor + ' !important;' +
        'background-color: ' + bgColor + ' !important;' +
        'font-size: ' + fontSizePx + 'px !important;' +
        'font-family: "AnjomanMax", sans-serif !important;' +
        '}' +
        'video::-webkit-media-text-track-display {' +
        'display: inline-block !important;' +
        'background-color: transparent !important;' +
        'font-size: ' + fontSizePx + 'px !important;' +
        'text-align: center !important;' +
        '}' +
        'video::-webkit-media-text-track-container {' +
        'font-size: ' + fontSizePx + 'px !important;' +
        'bottom: 10vh !important;' +
        'top: auto !important;' +
        'text-align: center !important;' +
        '}';
}

/**
 * Main player component
 */
function MnbPlayer(props) {
    var {
        source,
        poster,
        subtitle,
        behavior,
        autoPlay = true,
        children,
        onReady,
        onPlay,
        onPause,
        onTimeUpdate,
        onEnded,
        onError,
        isLive = false,
        className = '',
    } = props;

    var player = usePlayer();
    var videoRef = useRef(null);
    var hlsProviderRef = useRef(null);
    var videoProviderRef = useRef(null);
    var isInitializedRef = useRef(false);
    var previousSourceRef = useRef('');
    var subtitleRef = useRef(subtitle); // Track subtitle without triggering effect

    // Subscribe to state for callbacks
    var isReady = usePlayerState('isReady');
    var playing = usePlayerState('playing');
    var paused = usePlayerState('paused');
    var currentTime = usePlayerState('currentTime');
    var ended = usePlayerState('ended');
    var error = usePlayerState('error');

    // Trigger callbacks on state changes
    useEffect(function () {
        if (isReady && onReady) onReady();
    }, [isReady, onReady]);

    useEffect(function () {
        if (playing && onPlay) onPlay();
    }, [playing, onPlay]);

    useEffect(function () {
        if (paused && !ended && onPause) onPause();
    }, [paused, ended, onPause]);

    useEffect(function () {
        if (onTimeUpdate) onTimeUpdate({ currentTime: currentTime });
    }, [currentTime, onTimeUpdate]);

    useEffect(function () {
        if (ended && onEnded) onEnded();
    }, [ended, onEnded]);

    useEffect(function () {
        if (error && onError) onError(error);
    }, [error, onError]);

    // Reset subtitle styling for each playback session; do not preserve player settings.
    useEffect(function () {
        applySubtitleCueStyle(1, '#ffffff', 'rgba(0, 0, 0, 0.5)');
    }, []);

    // Initialize providers
    var initializePlayer = useCallback(function (videoEl, sourceUrl, sourceType, subtitles) {
        console.log('[MnbPlayer] Initializing with source:', sourceUrl, 'type:', sourceType);

        // Create VideoProvider
        var videoProvider = new VideoProvider(videoEl, player.state);
        videoProvider.setup();
        videoProviderRef.current = videoProvider;

        // Store video ref in context
        player.videoRef.current = videoEl;

        // Check source type
        var hasHlsMime = sourceType && (
            sourceType.indexOf('mpegurl') !== -1 ||
            sourceType.indexOf('mpegURL') !== -1
        );
        var hasHlsUrl = sourceUrl && sourceUrl.indexOf('.m3u8') !== -1;
        var isHLS = !!(hasHlsMime || hasHlsUrl);

        if (isHLS && HLSProvider.isSupported()) {
            console.log('[MnbPlayer] Using HLS.js provider');

            // Create HLS provider
            var hlsProvider = new HLSProvider(videoEl, player.state);
            hlsProvider.setup({ isLive: isLive });
            hlsProvider.loadSource(sourceUrl);
            hlsProviderRef.current = hlsProvider;

            // Store HLS ref in context
            player.hlsRef.current = hlsProvider.getInstance();

        } else if (isHLS && HLSProvider.isNativeHLSSupported()) {
            console.log('[MnbPlayer] Using native HLS');
            videoEl.src = sourceUrl;

        } else {
            console.log('[MnbPlayer] Using direct source (MP4)');
            videoProvider.setSource(sourceUrl, sourceType || 'video/mp4');
        }

        // Autoplay
        if (autoPlay) {
            setTimeout(function () {
                videoProvider.play().catch(function (err) {
                    console.warn('[MnbPlayer] Autoplay failed:', err.message);
                });
            }, 100);
        }

        // Add external subtitle tracks if provided
        if (subtitles && subtitles.length > 0) {
            console.log('[MnbPlayer] Adding external subtitles:', subtitles.length);
            
            var defaultSubLang = behavior && behavior.defaultSubLang;
            var defaultSubIsOff = isSubtitleOffValue(defaultSubLang);
            var processedSubtitles = subtitles.map(function(item, index) {
                var isDefaultSub = !defaultSubIsOff && defaultSubLang && matchesSubtitleLang(item, defaultSubLang);
                var isDefaultSubByFallback =
                    !defaultSubIsOff &&
                    defaultSubLang &&
                    subtitles.filter(function(x) {
                        return matchesSubtitleLang(x, defaultSubLang);
                    }).length < 1 &&
                    index === 0;

                return Object.assign({}, item, {
                    default: isDefaultSub || isDefaultSubByFallback || false
                });
            });

            videoProvider.addTextTracks(processedSubtitles);
        }

        isInitializedRef.current = true;
    }, [player, autoPlay, isLive, behavior]);

    // Handle source changes
    useEffect(function () {
        var videoEl = videoRef.current;
        if (!videoEl) return;

        // Parse source
        var sourceUrl = '';
        var sourceType = '';

        if (Array.isArray(source)) {
            var srcObj = source[0];
            if (srcObj) {
                sourceUrl = srcObj.src || srcObj;
                sourceType = srcObj.type || '';
            }
        } else if (typeof source === 'object' && source && source.src) {
            sourceUrl = source.src;
            sourceType = source.type || '';
        } else if (typeof source === 'string') {
            sourceUrl = source;
        }

        if (!sourceUrl) {
            console.warn('[MnbPlayer] No valid source');
            return;
        }

        // Skip if same source
        if (sourceUrl === previousSourceRef.current) {
            return;
        }
        previousSourceRef.current = sourceUrl;

        // Cleanup previous
        if (hlsProviderRef.current) {
            hlsProviderRef.current.destroy();
            hlsProviderRef.current = null;
        }
        if (videoProviderRef.current) {
            videoProviderRef.current.destroy();
            videoProviderRef.current = null;
        }

        // Reset state
        player.state.reset();

        // Initialize with subtitle from ref (not prop to avoid re-runs)
        initializePlayer(videoEl, sourceUrl, sourceType, subtitleRef.current);

    }, [source, player, initializePlayer]); // subtitle removed - uses ref instead

    // Cleanup on unmount
    useEffect(function () {
        return function () {
            console.log('[MnbPlayer] Unmounting, cleaning up...');
            previousSourceRef.current = '';

            if (hlsProviderRef.current) {
                hlsProviderRef.current.destroy();
                hlsProviderRef.current = null;
            }
            if (videoProviderRef.current) {
                videoProviderRef.current.destroy();
                videoProviderRef.current = null;
            }
        };
    }, []);

    return (
        React.createElement('div', {
            id: 'mnb-player-container',
            className: 'mnb-player-container ' + className,
            style: {
                position: 'relative',
                width: '100%',
                height: '100%',
                backgroundColor: '#000',
                overflow: 'hidden',
            },
        },
            React.createElement('video', {
                ref: videoRef,
                className: 'mnb-player-video',
                poster: poster,
                playsInline: true,
                preload: 'metadata',
                // Required for loading external VTT subtitles from CDN
                crossOrigin: 'anonymous',
                style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                },
            }),
            children
        )
    );
}

export { MnbPlayer };
